// src/components/auth/Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WelcomeHeader from '../layout/WelcomeHeader';
import styles from './Register.module.css';
import { authApi } from '../../api';
import { hasCookieConsent, COOKIE_CONSENT_EVENT } from '../../utils/cookieConsent';
import { setPendingEmailOptIn, applyPendingEmailOptIn } from '../../utils/pendingEmailOptIn';

function Register({ setLoggedIn, setUsername }) {
  const { t } = useTranslation();
  const [cookieConsentGiven, setCookieConsentGiven] = useState(hasCookieConsent());
  const [username, setLocalUsername] = useState('');
  const [email, setLocalEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [wantsEmailUpdates, setWantsEmailUpdates] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const navigate = useNavigate();

  // Auf Cookie-Zustimmung reagieren (Banner kann während des Register-Besuchs akzeptiert werden)
  useEffect(() => {
    const onChange = () => setCookieConsentGiven(hasCookieConsent());
    window.addEventListener(COOKIE_CONSENT_EVENT, onChange);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onChange);
  }, []);

  // Registrierung mit API Service
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cookieConsentGiven) return;

    // Passwort-Übereinstimmung prüfen
    if (password !== confirmPassword) {
      setErrorMessage(t('auth.passwordMismatch'));
      return;
    }

    const result = await authApi.register({ email, username, password });

    if (result.success) {
      // E-Mail-Präferenz direkt nach dem Anlegen merken (nicht erst nach der
      // Bestätigung): da /auth/register keine Session öffnet und der Login bis
      // zur Verifizierung mit EMAIL_NOT_VERIFIED fehlschlägt, wird die Einstellung
      // beim ersten erfolgreichen Login automatisch angewendet.
      setPendingEmailOptIn(username, wantsEmailUpdates);

      // Registrierung löst eine Verifizierungs-Mail aus - Login schlägt bis
      // zur Bestätigung mit EMAIL_NOT_VERIFIED fehl
      const loginResult = await authApi.login({ username, password });

      if (loginResult.success) {
        applyPendingEmailOptIn(username);
        setUsername(username);
        setLoggedIn(true);
        navigate('/mein-regal');
      } else {
        setNeedsVerification(true);
      }
    } else {
      const errorMsg = result.error?.message || t('auth.registerFailed');
      setErrorMessage(errorMsg);
    }
  };

  if (needsVerification) {
    return (
      <>
        <WelcomeHeader />
        <div className={styles.authContainer}>
          <div className={styles.authCard}>
            <div className={styles.authHeader}>
              <h1 className={styles.authTitle}>{t('auth.almostDone')}</h1>
              <p className={styles.authSubtitle}>
                {t('auth.verificationSent', { email })}
              </p>
            </div>
            <button onClick={() => navigate('/login')} className={styles.submitButton}>
              {t('auth.goToLogin')}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <WelcomeHeader />
      <div className={styles.authContainer}>
      <div className={styles.authCard}>
        {/* Logo */}
        <div className={styles.authLogo}>
          <img src={require('../../assets/logo_1.png')} alt="MangaShelf" className={styles.authLogoImg} />
        </div>

        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>{t('auth.registerTitle')}</h1>
          <p className={styles.authSubtitle}>{t('auth.registerSubtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setLocalUsername(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.chooseUsername')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setLocalEmail(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.enterEmail')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.createPassword')}
              minLength="6"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              minLength="6"
            />
          </div>

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={wantsEmailUpdates}
                onChange={(e) => setWantsEmailUpdates(e.target.checked)}
              />
              <span>{t('auth.emailOptIn')}</span>
            </label>
          </div>

          {errorMessage && (
            <div className={styles.errorMessage}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
              {errorMessage}
            </div>
          )}

          {!cookieConsentGiven && (
            <p className={styles.cookieConsentHint}>{t('auth.cookieConsentRequired')}</p>
          )}

          <button type="submit" className={styles.submitButton} disabled={!cookieConsentGiven}>
            {t('auth.createAccount')}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <span>{t('auth.or')}</span>
        </div>

        {/* Actions */}
        <div className={styles.authActions}>
          <p className={styles.authPrompt}>
            {t('auth.alreadyHaveAccount')} <button onClick={() => navigate('/login')} className={styles.linkButton}>{t('auth.signIn')}</button>
          </p>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            {t('auth.backToHome')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default Register;
